import React, { useState, useEffect } from 'react';
import { useAuth } from '../Auth/AuthProvider';
import * as FiIcons from 'react-icons/fi';
import { 
  getMfaStatus, 
  enrollMfa, 
  verifyMfaEnrollment,
  unenrollMfa 
} from '../../services/platformAdminService';

const {
  FiShield,
  FiRefreshCw,
  FiCheck,
  FiX,
  FiAlertTriangle,
  FiSmartphone,
  FiKey,
  FiLock,
  FiUnlock
} = FiIcons;

const MfaSetup = () => {
  const { user, superadminRole } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Enrollment state
  const [enrollmentData, setEnrollmentData] = useState(null);
  const [verifyCode, setVerifyCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  
  // Unenroll state
  const [showUnenroll, setShowUnenroll] = useState(false);
  const [unenrollCode, setUnenrollCode] = useState('');
  const [unenrolling, setUnenrolling] = useState(false);

  const fetchStatus = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getMfaStatus();
      setStatus(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, []);

  const handleStartEnrollment = async () => {
    setError(null);
    try {
      const data = await enrollMfa();
      setEnrollmentData(data);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleVerifyEnrollment = async (e) => {
    e.preventDefault();
    if (!verifyCode || verifyCode.length !== 6) {
      setError('Please enter a 6-digit code');
      return;
    }
    
    setVerifying(true);
    setError(null);
    try {
      await verifyMfaEnrollment(enrollmentData.factor_id, verifyCode);
      setEnrollmentData(null);
      setVerifyCode('');
      await fetchStatus();
    } catch (err) {
      setError(err.message);
    } finally {
      setVerifying(false);
    }
  };

  const handleUnenroll = async (e) => {
    e.preventDefault();
    if (!unenrollCode || unenrollCode.length !== 6) {
      setError('Please enter a 6-digit code');
      return;
    }
    
    setUnenrolling(true);
    setError(null);
    try {
      await unenrollMfa(status.verified_factor_id, unenrollCode);
      setShowUnenroll(false);
      setUnenrollCode('');
      await fetchStatus();
    } catch (err) {
      setError(err.message);
    } finally {
      setUnenrolling(false);
    }
  };

  const cancelEnrollment = () => {
    setEnrollmentData(null);
    setVerifyCode('');
    setError(null);
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <FiRefreshCw className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-2xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-3 bg-purple-100 rounded-lg">
            <FiShield className="w-6 h-6 text-purple-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Two-Factor Authentication</h1>
            <p className="text-gray-500">Secure your superadmin account with TOTP</p>
          </div>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <FiAlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-red-700 font-medium">Error</p>
            <p className="text-red-600 text-sm">{error}</p>
          </div>
        </div>
      )}

      {/* Current Status */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Current Status</h2>
        
        <div className="flex items-center gap-4">
          {status?.mfa_enabled ? (
            <>
              <div className="p-3 bg-green-100 rounded-full">
                <FiCheck className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="font-medium text-green-700">MFA Enabled</p>
                <p className="text-sm text-gray-500">Your account is protected with two-factor authentication</p>
              </div>
            </>
          ) : (
            <>
              <div className="p-3 bg-yellow-100 rounded-full">
                <FiAlertTriangle className="w-6 h-6 text-yellow-600" />
              </div>
              <div>
                <p className="font-medium text-yellow-700">MFA Not Enabled</p>
                <p className="text-sm text-gray-500">Your account is not protected. Enable MFA to continue using superadmin features.</p>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Enrollment Flow */}
      {!status?.mfa_enabled && !enrollmentData && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Enable Two-Factor Authentication</h2>
          
          <div className="space-y-4 mb-6">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <FiSmartphone className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="font-medium text-gray-900">Step 1: Install an authenticator app</p>
                <p className="text-sm text-gray-500">Download Google Authenticator, Authy, or any TOTP app on your phone</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <FiKey className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="font-medium text-gray-900">Step 2: Scan QR code</p>
                <p className="text-sm text-gray-500">Use your authenticator app to scan the QR code we'll show you</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <FiLock className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="font-medium text-gray-900">Step 3: Verify setup</p>
                <p className="text-sm text-gray-500">Enter the 6-digit code from your app to complete setup</p>
              </div>
            </div>
          </div>
          
          <button
            onClick={handleStartEnrollment}
            className="w-full py-3 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition-colors flex items-center justify-center gap-2"
          >
            <FiShield className="w-5 h-5" />
            Start Setup
          </button>
        </div>
      )}

      {/* QR Code Display */}
      {enrollmentData && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Scan QR Code</h2>
          
          <div className="flex flex-col items-center mb-6">
            <div className="bg-white p-4 rounded-lg border-2 border-gray-200 mb-4">
              <img 
                src={enrollmentData.qr_code} 
                alt="MFA QR Code" 
                className="w-48 h-48"
              />
            </div>
            
            <p className="text-sm text-gray-500 text-center mb-2">
              Scan this QR code with your authenticator app
            </p>
            
            <details className="text-center">
              <summary className="text-sm text-blue-600 cursor-pointer hover:underline">
                Can't scan? Enter code manually
              </summary>
              <code className="block mt-2 p-2 bg-gray-100 rounded text-xs font-mono break-all">
                {enrollmentData.secret}
              </code>
            </details>
          </div>
          
          <form onSubmit={handleVerifyEnrollment} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Enter 6-digit code from your app
              </label>
              <input
                type="text"
                value={verifyCode}
                onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                maxLength={6}
                className="w-full px-4 py-3 text-center text-2xl font-mono tracking-widest border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                autoFocus
              />
            </div>
            
            <div className="flex gap-3">
              <button
                type="button"
                onClick={cancelEnrollment}
                className="flex-1 py-3 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={verifying || verifyCode.length !== 6}
                className="flex-1 py-3 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
              >
                {verifying ? (
                  <>
                    <FiRefreshCw className="w-5 h-5 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  <>
                    <FiCheck className="w-5 h-5" />
                    Verify & Enable
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Disable MFA */}
      {status?.mfa_enabled && !showUnenroll && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Manage MFA</h2>
          
          <p className="text-gray-600 mb-4">
            If you need to change your authenticator device, you can disable MFA and set it up again.
          </p>
          
          <button
            onClick={() => setShowUnenroll(true)}
            className="px-4 py-2 bg-red-50 text-red-600 rounded-lg font-medium hover:bg-red-100 transition-colors flex items-center gap-2"
          >
            <FiUnlock className="w-4 h-4" />
            Disable MFA
          </button>
        </div>
      )}

      {/* Unenroll Confirmation */}
      {showUnenroll && (
        <div className="bg-white rounded-xl shadow-sm border border-red-200 p-6">
          <div className="flex items-start gap-3 mb-4">
            <FiAlertTriangle className="w-6 h-6 text-red-500 flex-shrink-0" />
            <div>
              <h2 className="text-lg font-semibold text-red-700">Disable MFA</h2>
              <p className="text-gray-600 text-sm">
                Enter your current authenticator code to disable MFA. You will need to set it up again to access superadmin features.
              </p>
            </div>
          </div>
          
          <form onSubmit={handleUnenroll} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Current 6-digit code
              </label>
              <input
                type="text"
                value={unenrollCode}
                onChange={(e) => setUnenrollCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                maxLength={6}
                className="w-full px-4 py-3 text-center text-2xl font-mono tracking-widest border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              />
            </div>
            
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => {
                  setShowUnenroll(false);
                  setUnenrollCode('');
                  setError(null);
                }}
                className="flex-1 py-3 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={unenrolling || unenrollCode.length !== 6}
                className="flex-1 py-3 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
              >
                {unenrolling ? (
                  <>
                    <FiRefreshCw className="w-5 h-5 animate-spin" />
                    Disabling...
                  </>
                ) : (
                  <>
                    <FiUnlock className="w-5 h-5" />
                    Disable MFA
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Info Box */}
      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="font-medium text-blue-800 mb-2">Why is MFA required?</h3>
        <p className="text-blue-700 text-sm">
          As a superadmin, you have access to sensitive platform operations. Two-factor authentication 
          adds an extra layer of security to protect against unauthorized access, even if your password 
          is compromised.
        </p>
      </div>
    </div>
  );
};

export default MfaSetup;
